// The sidenav's secondary links (below the main navigation). Links marked
// `workspace` are folded under the "Workspace" toggle when there are more
// than two of them. The free-trial notice is not a link and stays in the
// template.

export type SecondaryNavLinkId =
  | 'support'
  | 'websiteAnalysis'
  | 'noteTaking'
  | 'linkedIn'
  | 'teamDashboard'
  | 'integration'
  | 'backendMetrics'
  | 'modelAutomation'
  | 'invoices'
  | 'billing'
  | 'userManagement'
  | 'inviteUsers';

export interface SecondaryNavLink {
  id: SecondaryNavLinkId;
  /** A `sidenav_links` translation key when `translate` is set, else shown as is. */
  title: string;
  translate?: boolean;
  routerLink: string;
  /** Who sees it. Links without one have their own rule in the sidenav. */
  privilege?: string;
  materialIcon?: string;
  materialIconOutlined?: boolean;
  iconSrc?: string;
  /** Inline style for `iconSrc` images. */
  iconStyle?: string;
  /** Folded under "Workspace". */
  workspace?: boolean;
  cssClass?: string;
}

export const secondaryNavigationLinks: SecondaryNavLink[] = [
  {
    id: 'support',
    title: 'Support',
    routerLink: '/support-inbox',
    privilege: 'behamics_support_chat',
    materialIcon: 'support_agent',
    cssClass: 'support-nav-link',
  },
  // Moved here from the main navigation (hidden there with hideFromView).
  {
    id: 'websiteAnalysis',
    title: 'Website Analysis',
    translate: true,
    routerLink: '/website-analysis',
    privilege: 'websiteAnalysis',
    iconSrc: '../../assets/img/website_analysis_icon.svg',
    iconStyle: 'width:20px;height:16px',
    workspace: true,
  },
  {
    id: 'noteTaking',
    title: 'Note-taking',
    translate: true,
    routerLink: '/notetaking',
    privilege: 'notetaking',
    iconSrc: '../../assets/img/nudge_icon.svg',
    iconStyle: 'width:18px;height:18px',
    workspace: true,
  },
  {
    id: 'linkedIn',
    title: 'LinkedIn',
    translate: true,
    routerLink: '/linkedin',
    privilege: 'linkedinOverall',
    iconSrc: '../../assets/img/linkedIn_icon.svg',
    iconStyle: 'width:20px;height:16px',
    workspace: true,
  },
  {
    id: 'teamDashboard',
    title: 'Team Dashboard',
    routerLink: '/team-dashboard',
    privilege: 'team_dashboard',
    iconSrc: '../../assets/img/task_icon.svg',
    iconStyle: 'width:20px;height:20px',
    workspace: true,
  },
  {
    id: 'integration',
    title: 'Integration',
    routerLink: '/behamics-integration',
    privilege: 'behamicsScriptIntegration',
    iconSrc: '../../assets/img/integration_icon.svg',
    workspace: true,
  },
  {
    id: 'backendMetrics',
    title: 'Backend Metrics',
    translate: true,
    routerLink: '/metrics/backend',
    privilege: 'backendMetrics',
    materialIcon: 'query_stats',
    workspace: true,
  },
  {
    id: 'modelAutomation',
    title: 'Model Automation',
    translate: true,
    routerLink: '/model-automation',
    privilege: 'modelAutomationModels',
    materialIcon: 'settings_suggest',
    workspace: true,
  },
  {
    // Shown by `canShowInvoices` (several invoice privileges).
    id: 'invoices',
    title: 'Invoices',
    translate: true,
    routerLink: '/invoices',
    materialIcon: 'description',
    materialIconOutlined: true,
    workspace: true,
    cssClass: 'parent-incentive',
  },
  {
    // seo_billing, and for white-label targets only master accounts.
    id: 'billing',
    title: 'Billing',
    translate: true,
    routerLink: '/seo/billing',
    privilege: 'seo_billing',
    materialIcon: 'credit_card',
    workspace: true,
  },
  {
    id: 'userManagement',
    title: 'User Management',
    translate: true,
    routerLink: '/user-management',
    privilege: 'masterUserManagement',
    materialIcon: 'person',
    workspace: true,
  },
  {
    id: 'inviteUsers',
    title: 'Invite Users',
    translate: true,
    routerLink: '/user-permissions',
    privilege: 'userPermissions',
    materialIcon: 'person',
    workspace: true,
  },
];
